import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import {
  currentLanguage,
  isLanguage,
  LANGUAGE_NAMES,
  setLanguage,
  SUPPORTED_LANGUAGES,
  type Language
} from '@/shared/i18n';
import { cn } from '@/shared/lib/cn';

export interface LanguageSelectProps {
  /** Called after the interface has switched — e.g. to save the choice to the profile. */
  onChange?: (language: Language) => void;
  /** `gradient` for the auth header, `field` for a form or a settings row. */
  tone?: 'gradient' | 'field';
  /** Visible label above the control; without it the label is for screen readers only. */
  label?: string;
  className?: string;
}

/**
 * The language picker: every supported language, each named in its own words.
 *
 * A native `<select>` rather than a custom list. Fifteen options is what the
 * platform picker is good at — it scrolls, it searches by typing, it is a
 * proper wheel on a phone — and it is accessible without any of the work a
 * hand-built listbox needs.
 *
 * Choosing switches the interface at once. Saving the choice anywhere else —
 * the profile — is the caller's business, through `onChange`.
 */
export function LanguageSelect({
  onChange,
  tone = 'field',
  label,
  className
}: LanguageSelectProps) {
  const { t, i18n } = useTranslation();
  const id = useId();
  // Read through i18n so the control re-renders when the language changes elsewhere.
  const value = isLanguage(i18n.language) ? i18n.language : currentLanguage();

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={id}
        className={label === undefined ? 'sr-only' : 'text-body-sm font-semibold'}
      >
        {label ?? t('common.language')}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          if (!isLanguage(next)) return;
          void setLanguage(next).then(() => onChange?.(next));
        }}
        className={cn(
          'cursor-pointer rounded-pill text-body-sm font-semibold outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
          tone === 'gradient'
            ? 'h-10 bg-white/15 px-3 text-[var(--on-gradient)] hover:bg-white/25 focus-visible:outline-white [&>option]:text-[var(--text-primary)]'
            : 'h-11 border border-[var(--border)] bg-[var(--surface)] px-4 text-[var(--text-primary)] focus-visible:outline-[var(--focus-ring)]'
        )}
      >
        {SUPPORTED_LANGUAGES.map((language) => (
          <option key={language} value={language} lang={language}>
            {LANGUAGE_NAMES[language]}
          </option>
        ))}
      </select>
    </div>
  );
}
